import * as service from "../service";
import { store } from "../../store";
import { getAuthUserId } from "@/utils/authUser";

/**
 * Fetch the current user's chat rooms.
 * Backend: GET /api/chat/chatRooms/getChatRooms
 */
export const GetChatRoomsAPI = async () => {
  const userId = getAuthUserId(store.getState().auth as any);
  try {
    const resp = await service.call("GET", "api/chat/chatRooms/getChatRooms", {
      user_id: userId,
    });
    return resp;
  } catch (err) {
    console.error(`GetChatRoomsAPI ${err}`);
    return { result: [], status: 2 };
  }
};

/**
 * Idempotently create (or find) a one-to-one chat room. For `type = "admin"`
 * `receiver_user_id` may be omitted — the backend resolves the admin.
 * Backend: POST /api/chat/chatRooms/createChatRoom
 */
export const CreateChatRoomAPI = async ({
  user_id,
  receiver_user_id,
  type,
  coupon_id,
  sponsor_activity_id,
}: {
  user_id: string;
  receiver_user_id?: string;
  type: string;
  coupon_id?: string;
  sponsor_activity_id?: string;
}) => {
  try {
    const resp = await service.call("POST", "api/chat/chatRooms/createChatRoom", {
      user_id,
      receiver_user_id,
      type,
      coupon_id,
      sponsor_activity_id,
    });
    return resp;
  } catch (err) {
    console.error(`CreateChatRoomAPI ${err}`);
    return { result: null, status: 2 };
  }
};

/**
 * Fetch the messages of a chat room (newest first).
 * Backend: GET /api/chat/chatRooms/getMessages
 */
export const GetMessagesAPI = async (
  chat_room_id: string,
  page: number = 1,
) => {
  const userId = getAuthUserId(store.getState().auth as any);
  try {
    const resp = await service.call("GET", "api/chat/chatRooms/getMessages", {
      chat_room_id,
      user_id: userId,
      page,
    });
    return resp;
  } catch (err) {
    console.error(`GetMessagesAPI ${err}`);
    return { result: [], status: 2 };
  }
};

/**
 * Mark every message of a room (not sent by the user) as read.
 * Backend: POST /api/chat/chatRooms/markRead
 */
export const MarkChatReadAPI = async ({
  chat_room_id,
  user_id,
}: {
  chat_room_id: string;
  user_id: string;
}) => {
  try {
    const resp = await service.call("POST", "api/chat/chatRooms/markRead", {
      chat_room_id,
      user_id,
    });
    return resp;
  } catch (err) {
    console.error(`MarkChatReadAPI ${err}`);
    return { status: 2 };
  }
};

/**
 * Soft-delete the user's participant row in a chat room.
 * Backend: POST /api/chat/chatRooms/leaveChatRoom
 */
export const LeaveChatRoomAPI = async ({
  chat_room_id,
  user_id,
}: {
  chat_room_id: string;
  user_id: string;
}) => {
  try {
    const resp = await service.call("POST", "api/chat/chatRooms/leaveChatRoom", {
      chat_room_id,
      user_id,
    });
    return resp;
  } catch (err) {
    console.error(`LeaveChatRoomAPI ${err}`);
    return { status: 2 };
  }
};

/**
 * Fetch a one-to-one chat room by receiver (used by event / activity chat).
 * Backend: GET /api/chat/chatRooms/getChatRoom
 */
export const GetChatRoomAPI = async (
  receiver_user_id: string,
  chatRoomType: string,
) => {
  const userId = getAuthUserId(store.getState().auth as any);
  try {
    const resp = await service.call("GET", "api/chat/chatRooms/getChatRoom", {
      user_id: userId,
      receiver_user_id,
      chat_room_type: chatRoomType,
    });
    return resp;
  } catch (err) {
    console.error(`GetChatRoomAPI ${err}`);
    return { result: null, status: 2 };
  }
};

/**
 * Fetch (or create) a group chat room for an event / activity.
 * Backend: GET /api/chat/chatRooms/getChatRoomGroup
 */
export const GetChatRoomGroupAPI = async (info: {
  acceptedUserIds: string[];
  event_id: string;
  activity_id: string;
  chat_room_name: string;
  society_ids: string[];
}) => {
  try {
    const resp = await service.call(
      "GET",
      "api/chat/chatRooms/getChatRoomGroup",
      {
        accepted_user_ids: info.acceptedUserIds,
        event_id: !info.event_id ? null : info.event_id,
        activity_id: !info.activity_id ? null : info.activity_id,
        chat_room_name: info.chat_room_name,
        society_ids: info.society_ids,
      },
    );
    return resp;
  } catch (err) {
    console.error(`GetChatRoomGroupAPI ${err}`);
    return { result: null, status: 2 };
  }
};
